import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useRouter } from 'next/router'

import { pageHasHero } from '@/utils/page'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export const TransitionContext = createContext<undefined>(undefined)

export const TRANSITION_DURATION = 0.5

const TransitionProvider = ({
  pageProps,
  children
}: PropsWithChildren<{ pageProps: WPPageData }>) => {
  const router = useRouter()
  const nodeRef = useRef<HTMLElement>(null!)
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])
  const contextValue = useMemo(() => undefined, [])

  const handleTransitionEnter = useCallback(() => {
    console.log('handleTransitionEnter')
    document.documentElement.classList.remove('page-transition')
  }, [])

  const handleTransitionLeave = useCallback(() => {
    console.log('handleTransitionLeave')
    document.documentElement.classList.add('page-transition')
  }, [])

  useEffect(() => {
    ScrollTrigger.clearScrollMemory('manual')

    router.beforePopState((state) => {
      // console.log(state, window.history.scrollRestoration)
      state.options.scroll = false
      return true
    })
  }, [router])

  return (
    <TransitionContext.Provider value={contextValue}>
      <SwitchTransition mode="out-in">
        <Transition
          key={router.asPath}
          nodeRef={nodeRef}
          timeout={{
            enter: 0,
            exit: TRANSITION_DURATION * 1000
          }}
          onEnter={handleTransitionEnter}
          onExit={handleTransitionLeave}
          unmountOnExit
        >
          {(state) => (
            <main
              ref={nodeRef}
              className={cn(
                'static',
                'transition-opacity duration-500 ease-in-out-cubic',
                {
                  'pt-[var(--header-height)]': !hasHero
                },
                {
                  'opacity-0 duration-0': state === 'entering',
                  'opacity-100': state === 'entered',
                  'opacity-0 ease-out-cubic': state === 'exiting'
                }
              )}
            >
              {children}
            </main>
          )}
        </Transition>
      </SwitchTransition>
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
